// ──────────────────────────────────────────────────────────────
// src/pages/profesor/ProfesorHistorialPage.tsx
// Historial de clases impartidas por el profesor logueado
// ──────────────────────────────────────────────────────────────
import { useState } from "react";
import dayjs from "dayjs";
import {
  Stack,
  Typography,
  IconButton,
  CircularProgress,
  Button,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
} from "@mui/material";
import { ArrowBack, ArrowForward } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { useReservas } from "../../hooks/useReservas";
import { useAuth } from "../../context/AuthContext";
import ReservaDialog from "../../components/ReservaDialog";
import type { ReservaEvt } from "../../api/ReservaEvt";

// Extiende la fila con el flag opcional (puede no venir en algunas vistas)
type HistRow = ReservaEvt & { firmadoCliente?: boolean };

export default function ProfesorHistorialPage() {
  const { t } = useTranslation<"translation">();
  const { user } = useAuth();

  const [mes, setMes] = useState(dayjs()); // mes visible
  const [sel, setSel] = useState<HistRow | null>(null); // reserva seleccionada

  const desde = mes.startOf("month").format("YYYY-MM-DD");
  const hasta = mes.endOf("month").format("YYYY-MM-DD");

  // Nota: si tu backend requiere el id "profesor" (no el userId),
  // mapea user.id -> profesor.id antes de pasar el filtro.
  const { rows, loading } = useReservas(desde, hasta, {
    profesorId: user?.id,
  });

  const histRows = rows as HistRow[];

  if (loading) return <CircularProgress />;

  return (
    <>
      {/* navegación entre meses */}
      <Stack direction="row" alignItems="center" spacing={1} mb={2}>
        <IconButton onClick={() => setMes((m) => m.subtract(1, "month"))}>
          <ArrowBack />
        </IconButton>

        <Typography
          variant="h6"
          sx={{
            flexGrow: 1,
            textAlign: "center",
            textTransform: "capitalize",
          }}
        >
          {mes.format("MMMM YYYY")}
        </Typography>

        <IconButton onClick={() => setMes((m) => m.add(1, "month"))}>
          <ArrowForward />
        </IconButton>
      </Stack>

      {/* tabla con las reservas */}
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>{t("profesorHistorial.tabla.fecha")}</TableCell>
            <TableCell>{t("profesorHistorial.tabla.hora")}</TableCell>
            <TableCell>{t("profesorHistorial.tabla.alumno")}</TableCell>
            <TableCell>{t("profesorHistorial.tabla.asignatura")}</TableCell>
            <TableCell>{t("profesorHistorial.tabla.estadoFirmas")}</TableCell>
            <TableCell align="right">{t("profesorHistorial.tabla.acciones")}</TableCell>
          </TableRow>
        </TableHead>

        <TableBody>
          {histRows.map((r) => {
            const hIni = (r.horaInicio ?? "").slice(0, 5);
            const hFin = (r.horaFin ?? "").slice(0, 5);
            return (
              <TableRow key={r.id} hover>
                <TableCell>{dayjs(r.fecha).format("DD/MM/YYYY")}</TableCell>
                <TableCell>{hIni && hFin ? `${hIni}-${hFin}` : "—"}</TableCell>
                <TableCell>{r.nombreCliente}</TableCell>
                <TableCell>{r.nombreAsignatura}</TableCell>
                <TableCell>
                  {r.estado}
                  {/* marca si el propio profesor/cliente ya firmó (según flag disponible) */}
                  {r.firmadoCliente && " · ✔️"}
                </TableCell>
                <TableCell align="right">
                  <Button size="small" onClick={() => setSel(r)}>
                    {t("profesorHistorial.verFirmar")}
                  </Button>
                </TableCell>
              </TableRow>
            );
          })}
          {histRows.length === 0 && (
            <TableRow>
              <TableCell colSpan={6}>
                <Typography variant="body2" color="text.secondary" align="center">
                  {t("profesorHistorial.sinResultados") ?? "No hay clases en este periodo."}
                </Typography>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>

      {/* diálogo reutilizable • edición deshabilitada */}
      <ReservaDialog
        open={Boolean(sel)}
        reserva={sel ?? undefined}
        onClose={() => setSel(null)}
        onEdit={() => {}} // los profesores no editan desde aquí
      />
    </>
  );
}
